import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { fmtP } from '../../utils/formatters';

// Real-time ticker symbols — using the EXACT instrument IDs from your INSTRUMENTS array
const TICKER_SYMBOLS = [
  { id: 'EURUSD', label: 'EUR/USD' },
  { id: 'GBPUSD', label: 'GBP/USD' },
  { id: 'XAUUSD', label: 'XAU/USD' },
  { id: 'BTCUSDT', label: 'BTC/USD' },
  { id: 'SPX', label: 'US500' },
  { id: 'Nasdaq', label: 'NAS100' },
  { id: 'USOIL', label: 'WTI' },
  { id: 'ETHUSDT', label: 'ETH/USD' },
  { id: 'USDJPY', label: 'USD/JPY' },
  { id: 'AAPL', label: 'AAPL' },
  { id: 'TSLA', label: 'TSLA' },
  { id: 'NVDA', label: 'NVDA' },
];

// Fallback static ticker data (shown while marketData loads)
const FALLBACK_TICKER = [
  { symbol: 'EUR/USD', price: '1.08420', up: true },
  { symbol: 'GBP/USD', price: '1.27160', up: false },
  { symbol: 'XAU/USD', price: '$2,412.30', up: true },
  { symbol: 'BTC/USD', price: '$67,240', up: true },
  { symbol: 'US500', price: '5,487.20', up: false },
  { symbol: 'NAS100', price: '19,842.10', up: true },
  { symbol: 'WTI', price: '$78.42', up: false },
  { symbol: 'ETH/USD', price: '$3,512.60', up: true },
  { symbol: 'USD/JPY', price: '156.420', up: true },
  { symbol: 'AAPL', price: '$193.20', up: false },
  { symbol: 'TSLA', price: '$248.50', up: true },
  { symbol: 'NVDA', price: '$142.80', up: true },
];

const AnzoTradeLanding = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('forex');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loaderFading, setLoaderFading] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const carouselRef = useRef(null);

  // Get real-time market data from Redux (same store as Sidebar)
  const { marketData } = useSelector((state) => state.trade);

  // Build real-time ticker items from market data with proper fallbacks
  const tickerItems = (() => {
    const realData = TICKER_SYMBOLS.map((sym) => {
      const data = marketData[sym.id];
      const price = data?.price;
      const pct = data?.changePct ?? data?.changePercent ?? data?.change ?? 0;
      const hasData = price !== undefined && price !== null && price > 0;

      return {
        symbol: sym.label,
        price: hasData ? fmtP(price, data?.cat || 'forex') : null,
        up: pct >= 0,
        hasData,
      };
    }).filter(item => item.hasData);

    return realData.length > 0 ? realData : FALLBACK_TICKER;
  })();

  /* ─────────────────────────────────────────────
     Candlestick Loader Data
  ───────────────────────────────────────────── */
  const loaderCandles = [
    { h: 34, up: false },
    { h: 58, up: true },
    { h: 26, up: false },
    { h: 72, up: true },
    { h: 44, up: false },
    { h: 86, up: true },
    { h: 38, up: false },
    { h: 64, up: true },
    { h: 30, up: false },
  ];

  /* ─────────────────────────────────────────────
     Page Loader
  ───────────────────────────────────────────── */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const fadeTimer = setTimeout(() => setLoaderFading(true), 1900);
    const hideTimer = setTimeout(() => {
      setIsLoading(false);
      document.body.style.overflow = '';
    }, 2500);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
      document.body.style.overflow = '';
    };
  }, []);

  /* ─────────────────────────────────────────────
     Scroll Detection for Navbar
  ───────────────────────────────────────────── */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ─────────────────────────────────────────────
     Instruments Data
  ───────────────────────────────────────────── */
  const instruments = {
    forex: [
      { symbol: 'EURUSD', name: 'Euro vs US Dollar', desc: 'Engage in trading the most popular currency pair, the Euro against the US Dollar.', leverage: '1:400' },
      { symbol: 'GBPUSD', name: 'Great Britain Pound vs US Dollar', desc: 'Participate in trading one of the most popular currency pairs.', leverage: '1:400' },
      { symbol: 'USDJPY', name: 'US Dollar vs Japanese Yen', desc: 'Trade on the currency movements between the US Dollar and the Japanese Yen.', leverage: '1:400' },
      { symbol: 'AUDUSD', name: 'Australian Dollar vs US Dollar', desc: 'Participate in trading between the Australian Dollar and the US Dollar.', leverage: '1:400' },
      { symbol: 'EURGBP', name: 'Euro vs Great Britain Pound', desc: 'Trade the Euro against the British Pound.', leverage: '1:400' },
    ],
    indices: [
      { symbol: 'UK100', name: 'UK 100 Cash Index', desc: 'The UK 100 Cash Index tracks the top 100 companies on the London Stock Exchange.', leverage: '1:200' },
      { symbol: 'DE40', name: 'Germany 40 Cash Index', desc: 'The Germany 40 Cash Index represents the top 40 companies in Germany.', leverage: '1:200' },
      { symbol: 'USTEC', name: 'NAS100', desc: 'The NAS100 comprises 100 of the largest non-financial companies on the Nasdaq.', leverage: '1:200' },
      { symbol: 'US30', name: 'Dow Jones 30', desc: 'The Dow Jones 30 represents 30 large-cap U.S. companies.', leverage: '1:200' },
      { symbol: 'US500', name: 'SPX500', desc: 'The SPX500 includes 500 of the largest publicly traded companies in the U.S.', leverage: '1:200' },
    ],
    stocks: [
      { symbol: 'AAPL.OQ', name: 'Apple Inc.', desc: 'A leading technology company known for its innovative products.', leverage: '1:5' },
      { symbol: 'NVDA.OQ', name: 'NVIDIA Corporation', desc: 'Known for its graphics processing units (GPUs) and AI technology.', leverage: '1:5' },
      { symbol: 'TSLA.OQ', name: 'Tesla, Inc.', desc: 'An American electric vehicle and clean energy company.', leverage: '1:5' },
      { symbol: 'GOOG.OQ', name: 'Alphabet Inc.', desc: 'The parent company of Google, specializing in Internet services.', leverage: '1:5' },
      { symbol: 'PAYPAL', name: 'PayPal Holdings, Inc.', desc: 'An American company operating an online payment system.', leverage: '1:5' },
    ],
    commodities: [
      { symbol: 'BRENT', name: 'Crude Oil Brent Cash', desc: 'Brent crude oil is a benchmark for oil prices worldwide.', leverage: '1:200' },
      { symbol: 'USOIL.c', name: 'WTI Crude Oil Cash', desc: 'WTI crude oil is a key benchmark for oil prices in the United States.', leverage: '1:200' },
      { symbol: 'COCOA.f', name: 'Cocoa US Futures', desc: 'Cocoa futures track the price of cocoa beans.', leverage: '1:200' },
      { symbol: 'COFFEE.f', name: 'Coffee US Futures', desc: 'Coffee futures represent the price of Arabica coffee beans.', leverage: '1:200' },
      { symbol: 'NGAS.f', name: 'NGAS Futures', desc: 'Natural gas futures represent the price of natural gas.', leverage: '1:200' },
    ],
    crypto: [
      { symbol: 'BTCUSD', name: 'Bitcoin vs. USD', desc: 'Bitcoin, often called digital gold, is the first cryptocurrency.', leverage: '1:5' },
      { symbol: 'ETHUSD', name: 'Ethereum vs. USD', desc: 'Ethereum is known for smart contracts and decentralized applications.', leverage: '1:5' },
      { symbol: 'LTCUSD', name: 'Litecoin vs. USD', desc: 'LiteCoin offers faster transactions and lower fees.', leverage: '1:5' },
      { symbol: 'XRPUSD', name: 'Ripple vs. USD', desc: 'Ripple facilitates fast and low-cost international money transfers.', leverage: '1:5' },
      { symbol: 'XMRUSD', name: 'Monero vs. USD', desc: 'Monero prioritizes privacy and anonymity.', leverage: '1:5' },
    ],
    metals: [
      { symbol: 'XAUUSD', name: 'Gold vs US Dollar', desc: 'Gold remains a highly sought-after safe haven asset.', leverage: '1:5' },
      { symbol: 'XAGUSD', name: 'Silver vs US Dollar', desc: 'Silver is both a precious and industrial metal.', leverage: '1:5' },
      { symbol: 'XPDUSD', name: 'Palladium vs US Dollar', desc: 'Palladium is primarily used in automotive catalytic converters.', leverage: '1:5' },
      { symbol: 'XPTUSD', name: 'Platinum vs US Dollar', desc: 'Platinum is a rare precious metal with industrial applications.', leverage: '1:5' },
      { symbol: 'XAGEUR', name: 'Silver vs Euro', desc: 'Silver known for industrial applications and investment.', leverage: '1:5' },
    ],
  };

  const tabs = ['forex', 'indices', 'stocks', 'commodities', 'crypto', 'metals'];
  const tabLabels = ['Forex', 'Indices', 'Stocks', 'Commodities', 'Cryptocurrencies', 'Metals'];

  /* ─────────────────────────────────────────────
     Initialize AOS
  ───────────────────────────────────────────── */
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      offset: 100,
      easing: 'ease-out-cubic',
    });
  }, []);

  /* ─────────────────────────────────────────────
     Carousel Scroll Progress
  ───────────────────────────────────────────── */
  const updateProgress = useCallback(() => {
    const track = carouselRef.current;
    if (track) {
      const maxScroll = track.scrollWidth - track.clientWidth;
      setScrollProgress(maxScroll <= 0 ? 100 : Math.min((track.scrollLeft / maxScroll) * 100, 100));
    }
  }, []);

  useEffect(() => {
    const track = carouselRef.current;
    if (track) {
      track.addEventListener('scroll', updateProgress);
      updateProgress();
      return () => track.removeEventListener('scroll', updateProgress);
    }
  }, [activeTab, updateProgress]);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0;
      updateProgress();
    }
    AOS.refresh();
  }, [activeTab, updateProgress]);

  /* ─────────────────────────────────────────────
     Scroll carousel
  ───────────────────────────────────────────── */
  const scrollCarousel = (direction) => {
    const track = carouselRef.current;
    if (!track) return;
    const card = track.querySelector('.card');
    if (!card) return;
    const scrollAmount = card.offsetWidth + 24;
    track.scrollBy({ left: direction === 'next' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  /* ─────────────────────────────────────────────
     Sidebar handlers
  ───────────────────────────────────────────── */
  const closeSidebar = () => {
    setSidebarOpen(false);
    document.body.style.overflow = '';
  };

  const openSidebar = () => {
    setSidebarOpen(true);
    document.body.style.overflow = 'hidden';
  };

  return (
    <>
      {/* ════════════════ PAGE LOADER ════════════════ */}
      {isLoading && (
        <div
          className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white transition-opacity duration-600 ease-out ${
            loaderFading ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(139,92,246,0.12),transparent_62%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_75%,rgba(99,102,241,0.08),transparent_55%)]" />

          <div className="relative flex items-end gap-[7px] h-[120px] mb-9 max-sm:h-[92px] max-sm:gap-[5px]">
            {loaderCandles.map((c, i) => (
              <div
                key={i}
                className="flex flex-col items-center justify-end h-full"
                style={{ animation: `candleLoop 2.2s ease-in-out ${i * 0.13}s infinite`, transformOrigin: 'bottom' }}
              >
                <div className="w-[2px] flex-1 bg-[#8b5cf6]/40 rounded-full" />
                {c.up ? (
                  <div className="w-[11px] rounded-[2px] bg-white border-2 border-[#8b5cf6]" style={{ height: `${c.h}px` }} />
                ) : (
                  <div className="w-[11px] rounded-[2px]" style={{ height: `${c.h}px`, background: 'linear-gradient(180deg, #7c3aed, #6366f1)' }} />
                )}
                <div className="w-[2px] h-[9px] bg-[#8b5cf6]/40 rounded-full" />
              </div>
            ))}
          </div>

          <div className="relative text-[1.3rem] font-extrabold tracking-[7px] uppercase text-[#5b21b6] mb-1" style={{ animation: 'loaderPulse 2.2s ease-in-out infinite' }}>
            ANZO{' '}
            <span className="bg-gradient-to-r from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] bg-clip-text text-transparent">TRADE</span>
          </div>
          <div className="relative text-[0.7rem] font-mono font-bold tracking-[3px] uppercase text-[#94a3b8] mb-6">Loading Markets</div>

          <div className="relative w-[170px] h-[3px] bg-[#ede9fe] rounded-full overflow-hidden">
            <div className="h-full w-1/3 rounded-full" style={{ background: 'linear-gradient(90deg, #7c3aed, #8b5cf6, #6366f1)', animation: 'loaderBar 1.15s ease-in-out infinite' }} />
          </div>
        </div>
      )}

      {/* ════════════════ MAIN PAGE ════════════════ */}
      <div className="font-sans bg-white text-[#5b21b6] overflow-x-hidden antialiased">
        <style>{`
          @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-20px); } }
          @keyframes slideIn { from { transform: scaleX(0); } to { transform: scaleX(1); } }
          @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes candleLoop { 0% { transform: scaleY(0.25) translateY(8px); opacity: 0.35; } 20% { transform: scaleY(1) translateY(0); opacity: 1; } 50% { transform: scaleY(1) translateY(-10px); opacity: 1; } 80% { transform: scaleY(1) translateY(0); opacity: 1; } 100% { transform: scaleY(0.25) translateY(8px); opacity: 0.35; } }
          @keyframes loaderBar { 0% { transform: translateX(-110%); } 50% { transform: translateX(60%); } 100% { transform: translateX(230%); } }
          @keyframes loaderPulse { 0%, 100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.04); } }
          @keyframes tickerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          @keyframes menuPopIn { 0% { transform: scale(0.8); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
          @keyframes slideDown { 0% { opacity: 0; transform: translateY(-10px); } 100% { opacity: 1; transform: translateY(0); } }
          .card { flex: 0 0 calc(25% - 1.125rem); animation: fadeInUp 0.6s ease backwards; }
          .carousel::-webkit-scrollbar { height: 6px; background: #f1f5f9; }
          .carousel::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 20px; }
          .tabs::-webkit-scrollbar { height: 4px; background: #f1f5f9; }
          .tabs::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
          .triangle { width: 90px; height: 90px; border: 2px solid rgba(255,255,255,0.12); clip-path: polygon(50% 0%, 100% 100%, 0% 100%); animation: float 7s infinite; }
          .nav-scrolled { box-shadow: 0 10px 40px rgba(124, 58, 237, 0.12) !important; }
          @media (max-width: 1024px) { .card { flex: 0 0 calc(33.333% - 1rem); } }
          @media (max-width: 768px) { .card { flex: 0 0 calc(50% - 0.75rem); } }
          @media (max-width: 500px) { .card { flex: 0 0 85%; } }
        `}</style>

        {/* ════════════════ NAVBAR — FULLY RESPONSIVE ════════════════ */}
        <nav className={`sticky top-0 z-[1000] bg-white/95 backdrop-blur-xl border-b-2 border-[#e2e8f0] py-2 sm:py-3 w-full transition-all duration-300 ${scrolled ? 'nav-scrolled' : 'shadow-sm'}`}>
          <div className="h-[5rem] mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 flex justify-between items-center gap-2 sm:gap-3 lg:gap-6 min-h-[60px] xs:min-h-[68px] sm:min-h-[76px]">
            
            {/* ── LEFT: Logo (Responsive & Big) ── */}
            <Link 
              to="/" 
              className="no-underline flex items-center gap-2 shrink-0 flex-shrink-0" 
              data-aos="fade-right"
            >
              <img 
                src="/src/assets/img/logo/logo-white.svg" 
                alt="Anzo Trade" 
                className="object-contain h-auto transition-transform duration-300 hover:scale-105"
                style={{ 
                  width: 'clamp(120px, 12vw, 180px)',
                  height: 'clamp(140px, 9vw, 135px)'
                }} 
              />
            </Link>

            {/* ── CENTER: Desktop Navigation (lg and up) ── */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-4 flex-1 justify-center">
              <ul className="flex list-none gap-3 xl:gap-5 items-center">
                {[
                  { label: 'Markets', href: '#markets' },
                  { label: 'Accounts', href: '#gateway' },
                  { label: 'Platform', href: '#advantage' },
                  { label: 'Tools', href: '#potential' },
                  { label: 'Resources', href: '#instruments' },
                ].map((item, i) => (
                  <li key={i} className="group" data-aos="fade-down" data-aos-delay={100 + i * 50}>
                    <a 
                      href={item.href} 
                      className="no-underline text-[#0f172a] text-[0.85rem] xl:text-[0.95rem] font-semibold flex items-center gap-1 transition-all duration-300 py-1.5 px-1 border-b-2 border-transparent relative hover:text-[#8b5cf6] after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2px] after:bg-[#8b5cf6] after:transition-all after:duration-300 hover:after:w-full whitespace-nowrap"
                    >
                      {item.label}
                      <i className="fas fa-chevron-down text-[0.55rem] xl:text-[0.65rem] opacity-70 transition-transform duration-300 group-hover:rotate-180 group-hover:opacity-100" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* ── RIGHT: Auth Buttons + Mobile Toggle ── */}
            <div className="flex items-center gap-2 xs:gap-2.5 sm:gap-3 lg:gap-3.5 xl:gap-4 flex-shrink-0" data-aos="fade-left">
              
              {/* Login Button — Always visible & prominent on desktop */}
              <Link 
                to="/login" 
                className="bg-white border-2 border-[#8b5cf6] text-[#8b5cf6] py-2 xs:py-2.5 sm:py-2.75 lg:py-3 lg:px-6 px-3 xs:px-4 sm:px-5 xl:px-7 rounded-[40px] font-extrabold text-[0.7rem] xs:text-[0.8rem] sm:text-[0.9rem] lg:text-[0.95rem] xl:text-[1rem] cursor-pointer transition-all duration-300 inline-flex items-center gap-1.5 xs:gap-2 lg:gap-2.5 hover:bg-[#f5f3ff] hover:border-[#7c3aed] hover:text-[#7c3aed] hover:-translate-y-1 hover:shadow-lg hover:shadow-[#8b5cf6]/20 no-underline whitespace-nowrap"
              >
                <i className="fas fa-sign-in-alt text-[0.65rem] xs:text-[0.75rem] sm:text-[0.85rem] lg:text-[0.95rem]" /> 
                <span className="hidden sm:inline">Log In</span>
              </Link>

              {/* Sign Up Button — Always visible & prominent on desktop */}
              <Link 
                to="/register" 
                className="inline-flex bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white py-2 xs:py-2.5 sm:py-2.75 lg:py-3 px-3 xs:px-4 sm:px-5 lg:px-6 xl:px-7 rounded-[40px] font-extrabold text-[0.7rem] xs:text-[0.8rem] sm:text-[0.9rem] lg:text-[0.95rem] xl:text-[1rem] cursor-pointer transition-all duration-300 items-center gap-1.5 xs:gap-2 lg:gap-2.5 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#8b5cf6]/35 hover:scale-105 no-underline whitespace-nowrap"
              >
                <i className="fas fa-user-plus text-[0.65rem] xs:text-[0.75rem] sm:text-[0.85rem] lg:text-[0.95rem]" /> 
                <span className="hidden sm:inline">Sign Up</span>
              </Link>

              {/* Mobile Menu Toggle — Only visible on mobile/tablet */}
              <button 
                onClick={openSidebar} 
                className="lg:hidden bg-transparent border-none text-[1.2rem] xs:text-[1.4rem] sm:text-[1.6rem] text-[#0f172a] cursor-pointer p-1.5 xs:p-1.5 sm:p-2 flex items-center justify-center w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl hover:bg-[#f1f5f9] active:bg-[#e2e8f0] transition-all duration-200 relative group" 
                aria-label="Menu"
              >
                <div className="flex flex-col gap-[4px] xs:gap-[5px] w-[18px] xs:w-[20px]">
                  <span className={`block h-[2px] bg-[#0f172a] rounded-full transition-all duration-300 origin-center group-hover:bg-[#8b5cf6] ${sidebarOpen ? 'rotate-45 translate-y-[6px]' : ''}`} />
                  <span className={`block h-[2px] bg-[#0f172a] rounded-full transition-all duration-300 group-hover:bg-[#8b5cf6] ${sidebarOpen ? 'opacity-0 scale-x-0' : ''}`} />
                  <span className={`block h-[2px] bg-[#0f172a] rounded-full transition-all duration-300 origin-center group-hover:bg-[#8b5cf6] ${sidebarOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} />
                </div>
              </button>
            </div>
          </div>
        </nav>

        {/* ════════════════ TICKER STRIP ════════════════ */}
        <div className="w-full bg-white border-b-2 border-[#e2e8f0] overflow-hidden py-1.5 sm:py-2 lg:py-2.5 relative">
          <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-12 lg:w-16 bg-gradient-to-r from-white to-transparent z-[2] pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-12 lg:w-16 bg-gradient-to-l from-white to-transparent z-[2] pointer-events-none" />
          <div className="flex w-max gap-4 sm:gap-6 lg:gap-10" style={{ animation: 'tickerScroll 40s linear infinite' }}>
            {[...tickerItems, ...tickerItems].map((t, i) => (
              <div key={i} className="flex items-center gap-1 sm:gap-1.5 lg:gap-2 text-[0.65rem] sm:text-[0.75rem] lg:text-[0.8rem] whitespace-nowrap">
                <span className="font-mono font-extrabold text-[#0f172a]">{t.symbol}</span>
                <span className="font-mono font-semibold text-[#64748b]">{t.price}</span>
                <span className={`font-mono font-extrabold ${t.up ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {t.up ? '▲' : '▼'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ════════════════ SIDEBAR OVERLAY ════════════════ */}
        <div 
          className={`fixed inset-0 bg-[#0f172a]/50 backdrop-blur-sm z-[2000] transition-all duration-300 ${sidebarOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} 
          onClick={closeSidebar} 
        />

        {/* ════════════════ SIDEBAR (MOBILE DRAWER) ════════════════ */}
        <aside 
          className={`fixed top-0 h-screen bg-white z-[2100] p-4 xs:p-5 sm:p-6 flex flex-col transition-all duration-300 shadow-2xl overflow-y-auto w-[min(280px,85vw)] xs:w-[min(300px,85vw)] ${sidebarOpen ? 'left-0' : '-left-full'}`} 
          style={{ transitionTimingFunction: 'cubic-bezier(0.2, 0.9, 0.4, 1)' }}
        >
          {/* Sidebar Header */}
          <div className="flex justify-between items-center mb-5 xs:mb-6 sm:mb-8">
            <Link to="/" onClick={closeSidebar} className="no-underline flex items-center gap-2">
              <img 
                src="/src/assets/img/logo/logo-white.svg" 
                alt="Anzo Trade" 
                className="object-contain"
                style={{ width: 'clamp(70px, 10vw, 110px)', height: 'clamp(52px, 7.5vw, 82px)' }} 
              />
            </Link>
            <button 
              onClick={closeSidebar} 
              className="bg-[#f1f5f9] border-none text-[1rem] xs:text-[1.1rem] w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 rounded-lg xs:rounded-lg sm:rounded-xl cursor-pointer transition-all hover:bg-[#e2e8f0] hover:rotate-90 flex items-center justify-center font-semibold text-[#0f172a]"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          {/* Sidebar Navigation */}
          <ul className="list-none flex flex-col gap-0.5">
            {[
              { icon: 'fa-globe', label: 'Markets', href: '#markets' },
              { icon: 'fa-wallet', label: 'Accounts', href: '#gateway' },
              { icon: 'fa-laptop', label: 'Platform', href: '#advantage' },
              { icon: 'fa-tools', label: 'Tools', href: '#potential' },
              { icon: 'fa-book', label: 'Resources', href: '#instruments' },
            ].map((item, i) => (
              <li key={i} style={{ animation: sidebarOpen ? `slideDown 0.3s ease ${i * 0.1}s both` : 'none' }}>
                <a 
                  href={item.href} 
                  onClick={closeSidebar} 
                  className="flex items-center gap-2.5 xs:gap-3 py-2.5 xs:py-3 px-3 xs:px-4 text-[#0f172a] no-underline font-semibold text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] rounded-lg xs:rounded-xl sm:rounded-2xl transition-all duration-200 hover:bg-[#f5f3ff] hover:translate-x-1.5 hover:text-[#8b5cf6] active:scale-[0.98]"
                >
                  <i className={`fas ${item.icon} text-[#8b5cf6] text-[0.9rem] xs:text-[1rem] sm:text-[1.1rem] w-5 text-center`} /> 
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile-only Sign Up button inside sidebar */}
          <div className="xs:hidden mt-5 pt-4 border-t border-[#e2e8f0]">
            <Link 
              to="/register" 
              onClick={closeSidebar} 
              className="bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white py-3 px-5 rounded-lg font-extrabold text-[0.85rem] w-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#8b5cf6]/25 flex items-center justify-center gap-2 no-underline active:scale-[0.98]"
            >
              <i className="fas fa-user-plus" /> Sign Up Free
            </Link>
          </div>

          {/* Bottom Auth Buttons */}
          <div className="mt-auto pt-4 xs:pt-5 sm:pt-6 flex flex-col gap-2 xs:gap-2.5 sm:gap-3">
            <Link 
              to="/login" 
              onClick={closeSidebar} 
              className="bg-white border-2 border-[#8b5cf6] text-[#8b5cf6] py-2.5 xs:py-3 sm:py-3.5 rounded-lg xs:rounded-lg sm:rounded-xl font-extrabold text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] w-full cursor-pointer transition-all duration-300 hover:bg-[#f5f3ff] text-center no-underline block active:scale-[0.98]"
            >
              <i className="fas fa-sign-in-alt mr-2" /> Log In
            </Link>
            <Link 
              to="/register" 
              onClick={closeSidebar} 
              className="hidden xs:flex bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white py-2.5 xs:py-3 sm:py-3.5 rounded-lg xs:rounded-lg sm:rounded-xl font-extrabold text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] w-full cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#8b5cf6]/25 text-center no-underline items-center justify-center gap-2 active:scale-[0.98]"
            >
              <i className="fas fa-user-plus" /> Sign Up
            </Link>
          </div>
        </aside>

        {/* ════════════════ HERO SECTION ════════════════ */}
        <section id="home" className="flex flex-col items-center justify-center text-center pt-8 xs:pt-10 sm:pt-12 lg:pt-16 px-3 xs:px-4 sm:px-6 bg-gradient-to-br from-[#f5f3ff] via-white to-white relative overflow-hidden">
          <div className="relative z-10 max-w-[850px] w-full">
            <div className="inline-flex items-center gap-1 xs:gap-1.5 sm:gap-2 px-2.5 xs:px-3 sm:px-4 py-1 sm:py-1.5 bg-[#f5f3ff] border-2 border-[#ede9fe] rounded-full mb-3 xs:mb-4 sm:mb-6" data-aos="fade-up">
              <i className="fas fa-bolt text-[#8b5cf6] text-[7px] xs:text-[8px] sm:text-[10px]" />
              <span className="text-[7px] xs:text-[8px] sm:text-[10px] font-mono font-extrabold text-[#8b5cf6] tracking-wider uppercase">NEXT-GEN TRADING</span>
            </div>
            <h1 className="text-[clamp(1.6rem,5vw,4rem)] font-extrabold text-[#0f172a] leading-[1.15] mb-3 xs:mb-4 sm:mb-5 tracking-[-0.5px]" data-aos="fade-up" data-aos-duration="800">
              A World-Leading<br />
              <span className="bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] bg-clip-text text-transparent">Trading Experience</span>
            </h1>
            <p className="text-[clamp(0.85rem,2vw,1.2rem)] text-[#475569] font-semibold mb-5 xs:mb-6 sm:mb-8 max-w-[600px] mx-auto" data-aos="fade-up" data-aos-delay="200">
              Forex, Stocks, Commodities, Crypto & Indices — backed by innovative tools, powerful platform and ironclad security.
            </p>
            <div className="flex flex-wrap gap-2 xs:gap-2.5 sm:gap-3 lg:gap-4 items-center justify-center mb-6 xs:mb-8 sm:mb-10" data-aos="fade-up" data-aos-delay="400">
              <Link to="/register" className="bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white py-2 xs:py-2.5 sm:py-3 lg:py-4 px-5 xs:px-7 sm:px-9 lg:px-11 font-extrabold text-xs xs:text-sm sm:text-base rounded-[50px] cursor-pointer inline-flex items-center gap-1.5 sm:gap-2 shadow-xl shadow-[#8b5cf6]/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#8b5cf6]/40 no-underline border-b-[3px] border-[#7c3aed]">
                <i className="fas fa-rocket" /> Get Started Free
              </Link>
              <Link to="/login" className="bg-white border-2 border-purple-500 text-purple-600 py-[0.7rem] xs:py-[0.8rem] sm:py-[0.9rem] lg:py-[0.95rem] px-5 xs:px-7 sm:px-9 lg:px-11 rounded-[50px] font-extrabold text-xs xs:text-sm sm:text-base cursor-pointer inline-flex items-center gap-1.5 sm:gap-2.5 shadow-md hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 hover:bg-purple-50 hover:-translate-y-1 hover:scale-105 no-underline">
                <i className="fas fa-sign-in-alt" /> Login Now
              </Link>
            </div>
          </div>
          <div className="relative z-10 w-full max-w-[950px] mt-1 sm:mt-2 px-3 xs:px-0" data-aos="zoom-in" data-aos-delay="600">
            <img src="./src/assets/img/hero-img.png" alt="Trading dashboard" className="w-full h-auto rounded-[12px] xs:rounded-[16px] sm:rounded-[20px] lg:rounded-[28px] transition-transform duration-500 hover:scale-[1.01]" />
          </div>
        </section>

        {/* ════════════════ MARKETS SECTION ════════════════ */}
        <section id="markets" className="bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)] relative overflow-hidden">
          <div className="absolute right-[3%] top-1/2 -translate-y-1/2 z-[1] flex flex-col gap-11 max-lg:hidden">
            <div className="triangle" /><div className="triangle" /><div className="triangle" />
          </div>
          <div className="max-w-[1200px] mx-auto relative z-10">
            <div className="mb-8 xs:mb-10 sm:mb-12 lg:mb-14 text-left max-md:text-center" data-aos="fade-right">
              <div className="text-[0.75rem] xs:text-[0.85rem] sm:text-[1rem] lg:text-[1.1rem] font-extrabold text-violet-200 tracking-[1.5px] xs:tracking-[2px] sm:tracking-[2.5px] mb-1 sm:mb-2 uppercase">DISCOVER</div>
              <h2 className="text-[clamp(1.5rem,5vw,3.6rem)] font-extrabold text-white leading-[1.2] mb-3 xs:mb-4 sm:mb-5">A Better Way to Trade</h2>
              <p className="text-[0.85rem] xs:text-[0.95rem] sm:text-[1rem] lg:text-[1.1rem] text-white/80 font-semibold max-w-[650px] mb-5 xs:mb-6 sm:mb-7 lg:mb-9 max-md:mx-auto">Streamlined journey with advanced charts, real-time alerts, and precise risk controls. Trade with confidence using Anzo Trade.</p>
              <Link to="/register" className="bg-white text-[#7c3aed] border-none py-[0.6rem] xs:py-[0.7rem] sm:py-[0.8rem] lg:py-[0.9rem] px-[1.5rem] xs:px-[1.8rem] sm:px-[2.2rem] lg:px-[2.6rem] rounded-[50px] font-extrabold text-xs xs:text-sm sm:text-base shadow-lg shadow-black/15 cursor-pointer transition-all duration-300 hover:bg-[#f0f4ff] hover:scale-105 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/20 inline-block no-underline">
                Open Account
              </Link>
            </div>
            <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-3 xs:gap-4 sm:gap-6 lg:gap-8 mt-6 xs:mt-8 sm:mt-10 lg:mt-11">
              {[
                { img: './src/assets/img/markets-1.svg', label: 'Forex' },
                { img: './src/assets/img/markets-2.svg', label: 'Commodities' },
                { img: './src/assets/img/markets-3.svg', label: 'Indices' },
                { img: './src/assets/img/markets-4.svg', label: 'Stocks' },
                { img: './src/assets/img/markets-5.svg', label: 'Crypto CFDs' },
                { img: './src/assets/img/markets-6.svg', label: 'Metals' },
              ].map((m, i) => (
                <a key={i} href="#" className="flex flex-col items-center gap-2 xs:gap-2.5 sm:gap-4 lg:gap-5 text-white no-underline transition-all duration-400 hover:-translate-y-[10px] sm:hover:-translate-y-[15px] hover:scale-[1.03] group" data-aos="flip-left" data-aos-delay={100 + i * 100}>
                  <div className="w-[60px] h-[60px] xs:w-[70px] xs:h-[70px] sm:w-[85px] sm:h-[85px] lg:w-[110px] lg:h-[110px] flex items-center justify-center rounded-lg xs:rounded-xl sm:rounded-2xl lg:rounded-3xl bg-white/8 backdrop-blur-sm transition-all duration-400 group-hover:bg-white/[0.18] group-hover:scale-110 group-hover:rotate-[5deg] group-hover:shadow-2xl group-hover:shadow-black/30">
                    <img src={m.img} alt={m.label} className="w-[45px] h-[45px] xs:w-[52px] xs:h-[52px] sm:w-[65px] sm:h-[65px] lg:w-[100px] lg:h-[100px] object-contain brightness-125 transition-transform duration-400 group-hover:scale-115" />
                  </div>
                  <span className="font-extrabold text-[0.8rem] xs:text-[0.9rem] sm:text-[1rem] lg:text-[1.15rem] text-center">{m.label}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════ GATEWAY SECTION ════════════════ */}
        <section id="gateway" className="bg-[#f1f5f9] py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)] flex items-center justify-center">
          <div className="max-w-[1350px] w-full grid grid-cols-1 md:grid-cols-2 gap-[clamp(1.5rem,6vw,5rem)] items-center">
            <div className="flex justify-center items-center md:order-1" data-aos="fade-right" data-aos-delay="200">
              <img src="./src/assets/img/mbl.png" alt="Anzo Trade Platform" className="w-full max-w-[550px] h-auto object-contain" style={{ animation: 'float 6s ease-in-out infinite' }} />
            </div>
            <div className="flex flex-col gap-4 xs:gap-5 sm:gap-6 max-md:items-center max-md:text-center md:order-2" data-aos="fade-left">
              <h2 className="text-[clamp(1.4rem,5vw,3.2rem)] font-extrabold text-[#0f172a] leading-[1.2] tracking-[-0.5px]">Your Gateway to Global Markets</h2>
              <p className="text-[0.95rem] xs:text-[1rem] sm:text-[1.05rem] lg:text-[1.1rem] text-[#475569] leading-[1.7] font-semibold max-w-[550px] max-md:max-w-full">
                Access global markets anytime, anywhere on desktop or mobile — backed by secure deposits, trusted fund protection, and multiple payment methods.
              </p>
              <div className="grid grid-cols-3 gap-2 xs:gap-2.5 sm:gap-3 w-full max-w-[440px]">
                {[['160+', 'Instruments'], ['1:400', 'Leverage'], ['24/7', 'Support']].map(([v, l], i) => (
                  <div key={i} className="text-center bg-white rounded-lg xs:rounded-xl sm:rounded-2xl border-2 border-[#e2e8f0] py-3 xs:py-3.5 sm:py-4 px-2">
                    <div className="text-[1rem] xs:text-[1.15rem] sm:text-[1.25rem] font-extrabold text-[#8b5cf6]">{v}</div>
                    <div className="text-[0.6rem] xs:text-[0.65rem] sm:text-[0.68rem] font-bold text-[#64748b] uppercase tracking-wide mt-0.5 xs:mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <Link to="/register" className="inline-flex items-center justify-center w-fit px-[1.8rem] xs:px-[2.2rem] sm:px-[2.4rem] lg:px-[2.6rem] py-[0.7rem] xs:py-[0.8rem] sm:py-[0.85rem] lg:py-[0.9rem] bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white border-none rounded-[50px] font-extrabold text-xs xs:text-sm sm:text-base cursor-pointer shadow-xl shadow-[#8b5cf6]/25 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#8b5cf6]/35 max-md:w-full no-underline mt-1">
                Open Account
              </Link>
            </div>
          </div>
        </section>

        {/* ════════════════ ADVANTAGE SECTION ════════════════ */}
        <section id="advantage" className="bg-[#f8fafc] py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)]">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-10 xs:mb-12 sm:mb-14" data-aos="fade-up">
              <h2 className="text-[clamp(1.6rem,5vw,3rem)] font-extrabold text-[#0f172a] mb-3 xs:mb-4 sm:mb-5 tracking-[-0.5px]">Discover the Anzo Trade Advantage</h2>
              <p className="text-[0.9rem] xs:text-[0.95rem] sm:text-[1rem] lg:text-[1.1rem] leading-[1.7] text-[#475569] font-semibold max-w-[650px] mx-auto">
                We're committed to giving traders more than just a platform. We offer a secure, simple, and supportive environment built to empower every level of trader. Here's what sets us apart:
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 lg:grid-flow-dense gap-4 xs:gap-5 sm:gap-6 mb-10 xs:mb-12 sm:mb-14 max-w-[450px] sm:max-w-none mx-auto">
              {[
                { img: './src/assets/img/icon-1.svg', title: 'Secured & Regulated', desc: 'Your funds and data are protected with top-tier security measures and full compliance with industry regulations, so every trade sits on solid ground.', featured: true },
                { img: './src/assets/img/icon-2.svg', title: 'Fast Trading', desc: 'Trade without delays. Our ultra-fast execution ensures your orders are placed in real-time.' },
                { img: './src/assets/img/icon-3.svg', title: '24/7 Support', desc: 'Our team is available around the clock to assist whenever you need it.' },
                { img: './src/assets/img/icon-4.svg', title: 'Advanced Tools', desc: 'From powerful charts to precise risk controls, we give you the tools to trade smarter.' },
                { img: './src/assets/img/icon-5.svg', title: 'Execution Terms', desc: 'Benefit from low spreads and transparent pricing, getting the most out of every trade.' },
                { img: './src/assets/img/icon-6.svg', title: 'Anytime, Anywhere', desc: 'Access global markets from any device with our powerful web-based platform. No downloads, no limits.' },
              ].map((item, i) => (
                <div key={i} className={`bg-white rounded-lg xs:rounded-xl sm:rounded-2xl border-2 border-[#e2e8f0] transition-all duration-400 hover:shadow-2xl hover:shadow-[#8b5cf6]/10 hover:border-[#8b5cf6] hover:-translate-y-2 group ${item.featured ? 'lg:col-span-2 lg:row-span-2 p-6 xs:p-7 sm:p-8 lg:p-10 flex flex-col justify-center text-left bg-gradient-to-br from-[#f5f3ff] to-white' : 'p-5 xs:p-6 sm:p-7 lg:p-8 text-center'}`} data-aos="zoom-in" data-aos-delay={100 + i * 100}>
                  <div className={`${item.featured ? 'w-[70px] xs:w-[75px] sm:w-[80px] lg:w-[84px] h-[70px] xs:h-[75px] sm:h-[80px] lg:h-[84px] mb-4 xs:mb-5 sm:mb-6 lg:mb-7' : 'w-[60px] xs:w-[65px] sm:w-[70px] h-[60px] xs:h-[65px] sm:h-[70px] mx-auto mb-4 xs:mb-5 sm:mb-6'} flex items-center justify-center rounded-lg xs:rounded-xl sm:rounded-2xl transition-all duration-300 group-hover:bg-[#f5f3ff] group-hover:rotate-[10deg] group-hover:scale-110`}>
                    <img src={item.img} alt={item.title} className={`${item.featured ? 'w-[90px] xs:w-[100px] sm:w-[110px] lg:w-[110px] h-[90px] xs:h-[100px] sm:h-[110px] lg:h-[110px]' : 'w-[80px] xs:w-[90px] sm:w-[100px] h-[80px] xs:h-[90px] sm:h-[100px]'} object-contain`} />
                  </div>
                  <h3 className={`font-extrabold text-[#0f172a] tracking-[-0.3px] ${item.featured ? 'text-[1.3rem] xs:text-[1.4rem] sm:text-[1.5rem] mb-3 xs:mb-4' : 'text-[1rem] xs:text-[1.1rem] sm:text-[1.2rem] mb-2 xs:mb-3'}`}>{item.title}</h3>
                  <p className={`leading-[1.6] text-[#475569] font-semibold ${item.featured ? 'text-[0.95rem] xs:text-[0.98rem] sm:text-[1.02rem] max-w-[360px]' : 'text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem]'}`}>{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="700">
              <Link to="/register" className="inline-flex items-center gap-1.5 xs:gap-1.5 sm:gap-2 py-[0.7rem] xs:py-[0.75rem] sm:py-[0.8rem] lg:py-[0.9rem] px-[1.8rem] xs:px-[2.2rem] sm:px-[2.4rem] lg:px-[2.8rem] bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white border-none rounded-[50px] text-xs xs:text-sm sm:text-base font-extrabold cursor-pointer transition-all duration-300 shadow-xl shadow-[#8b5cf6]/25 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#8b5cf6]/35 no-underline">
                <i className="fas fa-chart-line" /> Trade Now
              </Link>
            </div>
          </div>
        </section>

        {/* ════════════════ POTENTIAL TOP ════════════════ */}
        <section id="potential" className="bg-gradient-to-br from-violet-50 to-white py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)]">
          <div className="max-w-[1200px] mx-auto">
            <div className="text-center mb-10 xs:mb-12 sm:mb-14" data-aos="fade-up">
              <h2 className="text-[clamp(1.5rem,5vw,2.8rem)] font-extrabold text-[#0f172a] mb-3 xs:mb-4 sm:mb-4 tracking-[-0.5px] leading-[1.2]">Unlock Your True Trading Potential</h2>
              <p className="text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] lg:text-base leading-[1.6] text-[#475569] font-semibold max-w-[500px] mx-auto">Enhance your trading skills and strategies with our advanced tools, educational resources, and daily market insights.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 xs:gap-5 sm:gap-6 mb-10 max-w-[450px] sm:max-w-none mx-auto">
              {[
                { img: './src/assets/img/img-1.png', title: 'Educational Articles', desc: 'Explore a rich library of educational content built to support your trading journey.' },
                { img: './src/assets/img/img-2.png', title: 'Glossary', desc: 'Understand the trading language with ease. Our glossary gives clear and simple definitions.' },
                { img: './src/assets/img/img-3.png', title: 'Portfolio Management', desc: 'Learn how to build and manage a strong trading portfolio with step-by-step guidance.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-lg xs:rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-400 shadow-md shadow-black/5 border-2 border-[#e2e8f0] hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#8b5cf6]/10 hover:border-[#8b5cf6] group" data-aos="fade-up" data-aos-delay={100 + i * 100}>
                  <img src={item.img} alt={item.title} className="w-full h-[150px] xs:h-[170px] sm:h-[200px] object-cover block transition-transform duration-500 group-hover:scale-[1.08]" />
                  <div className="p-4 xs:p-5 sm:p-6 lg:p-7">
                    <h3 className="text-[1rem] xs:text-[1.08rem] sm:text-[1.15rem] font-extrabold text-[#0f172a] mb-2 xs:mb-2.5 sm:mb-3 tracking-[-0.3px]">{item.title}</h3>
                    <p className="text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] leading-[1.6] text-[#475569] font-semibold mb-3 xs:mb-3.5 sm:mb-4">{item.desc}</p>
                    <a href="#" className="text-[#8b5cf6] no-underline text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] font-extrabold transition-all duration-300 hover:text-[#7c3aed] after:content-['_→'] after:inline-block after:transition-transform after:duration-300 hover:after:translate-x-1.5">Learn More</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════ POTENTIAL BOTTOM ════════════════ */}
        <section className="bg-white py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)] border-t-2 border-[#e2e8f0]">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex justify-between items-center mb-10 xs:mb-12 sm:mb-14 gap-6 sm:gap-8 flex-wrap max-md:flex-col max-md:items-start" data-aos="fade-right">
              <h2 className="text-[clamp(1.3rem,4vw,2.2rem)] font-extrabold text-[#0f172a] leading-[1.2] tracking-[-0.5px] max-w-[500px]">Optimize Your Trades With Expert Tools</h2>
              <Link to="/register" className="py-[0.7rem] xs:py-[0.75rem] sm:py-[0.8rem] lg:py-[0.9rem] px-[1.8rem] xs:px-[2.2rem] sm:px-[2.4rem] lg:px-[2.6rem] bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white border-none rounded-[50px] text-xs xs:text-sm sm:text-base font-extrabold cursor-pointer transition-all duration-300 shadow-xl shadow-[#8b5cf6]/25 hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#8b5cf6]/35 whitespace-nowrap no-underline">
                Open Account
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 xs:gap-5 sm:gap-6 max-w-[450px] sm:max-w-none mx-auto">
              {[
                { img: './src/assets/img/tools-1.svg', label: 'Economic Calendar' },
                { img: './src/assets/img/tools-2.svg', label: 'Chart Analysis' },
                { img: './src/assets/img/tools-3.svg', label: 'Risk Management' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 xs:gap-4 sm:gap-5 p-4 xs:p-5 sm:p-6 border-2 border-[#e2e8f0] rounded-lg xs:rounded-xl sm:rounded-2xl transition-all duration-300 bg-white hover:border-[#8b5cf6] hover:shadow-lg hover:shadow-[#8b5cf6]/10 hover:translate-x-1.5 group" data-aos="flip-left" data-aos-delay={100 + i * 100}>
                  <div className="w-[50px] h-[50px] xs:w-[52px] xs:h-[52px] sm:w-[55px] sm:h-[55px] flex items-center justify-center shrink-0 rounded-lg xs:rounded-lg sm:rounded-xl transition-all duration-300 group-hover:bg-[#f5f3ff] group-hover:-rotate-[5deg]">
                    <img src={item.img} alt={item.label} className="w-[45px] h-[45px] xs:w-[48px] xs:h-[48px] sm:w-[50px] sm:h-[50px] object-contain" />
                  </div>
                  <div className="text-sm xs:text-base sm:text-base font-extrabold text-[#0f172a]">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════ TRADING INSTRUMENTS ════════════════ */}
        <section id="instruments" className="max-w-[1300px] mx-auto py-[clamp(2.5rem,8vw,5rem)] px-[clamp(1rem,4vw,2.5rem)] bg-white">
          <h2 className="text-[clamp(1.6rem,5vw,3.2rem)] font-extrabold text-[#0f172a] mb-8 xs:mb-10 sm:mb-11 tracking-[-0.5px] leading-[1.2]" data-aos="fade-up">Trading Instruments</h2>

          <div className="flex gap-6 xs:gap-8 sm:gap-10 mb-8 xs:mb-10 border-b-2 border-[#e2e8f0] pb-0 overflow-x-auto tabs" data-aos="fade-up" data-aos-delay="100">
            {tabs.map((tab, i) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-[0.7rem] xs:py-[0.8rem] sm:py-[0.9rem] px-[0.2rem] border-none bg-transparent text-xs xs:text-sm sm:text-base font-extrabold cursor-pointer relative transition-all duration-300 whitespace-nowrap tracking-[-0.2px] ${activeTab === tab ? 'text-[#8b5cf6] after:content-[""] after:absolute after:-bottom-[2px] after:left-0 after:right-0 after:h-[3px] after:bg-[#8b5cf6] after:rounded-t-[3px]' : 'text-[#64748b] hover:text-[#0f172a]'}`}
                style={activeTab === tab ? { animation: 'slideIn 0.3s ease' } : {}}
              >
                {tabLabels[i]}
              </button>
            ))}
          </div>

          <div className="relative my-6 xs:my-7 sm:my-8 mx-0" data-aos="fade-up" data-aos-delay="200">
            <div className="absolute top-1/2 -left-[12px] xs:-left-[14px] sm:-left-[16px] lg:-left-[18px] -right-[12px] xs:-right-[14px] sm:-right-[16px] lg:-right-[18px] -translate-y-1/2 flex justify-between pointer-events-none z-[5] max-md:hidden">
              <button onClick={() => scrollCarousel('prev')} className="w-[38px] xs:w-[40px] sm:w-[42px] h-[38px] xs:h-[40px] sm:h-[42px] rounded-full border-2 border-[#e2e8f0] bg-white text-[#475569] cursor-pointer text-[1.1rem] xs:text-[1.2rem] sm:text-[1.3rem] flex items-center justify-center transition-all duration-300 pointer-events-auto shadow-md hover:border-[#8b5cf6] hover:text-[#8b5cf6] hover:bg-[#f5f3ff] hover:scale-110" aria-label="Previous">‹</button>
              <button onClick={() => scrollCarousel('next')} className="w-[38px] xs:w-[40px] sm:w-[42px] h-[38px] xs:h-[40px] sm:h-[42px] rounded-full border-2 border-[#e2e8f0] bg-white text-[#475569] cursor-pointer text-[1.1rem] xs:text-[1.2rem] sm:text-[1.3rem] flex items-center justify-center transition-all duration-300 pointer-events-auto shadow-md hover:border-[#8b5cf6] hover:text-[#8b5cf6] hover:bg-[#f5f3ff] hover:scale-110" aria-label="Next">›</button>
            </div>

            <div ref={carouselRef} className="flex gap-4 xs:gap-5 sm:gap-6 overflow-x-auto scroll-smooth pb-2 xs:pb-2.5 sm:pb-3 carousel">
              {(instruments[activeTab] || []).map((item, index) => (
                <div key={index} className="card bg-white rounded-[14px] xs:rounded-[16px] sm:rounded-[18px] lg:rounded-[20px] p-5 xs:p-6 sm:p-7 lg:p-8 flex flex-col min-h-[280px] xs:min-h-[300px] sm:min-h-[320px] lg:min-h-[340px] transition-all duration-400 border-2 border-[#e2e8f0] hover:border-[#8b5cf6] hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#8b5cf6]/10" style={{ animationDelay: `${index * 0.1}s` }}>
                  <h3 className="text-[1.15rem] xs:text-[1.25rem] sm:text-[1.3rem] lg:text-[1.4rem] font-extrabold text-[#0f172a] mb-1">{item.symbol}</h3>
                  <p className="text-[0.75rem] xs:text-[0.8rem] sm:text-[0.85rem] text-[#64748b] mb-3 xs:mb-3.5 sm:mb-4 font-semibold">{item.name}</p>
                  <p className="text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] leading-[1.6] text-[#475569] mb-5 xs:mb-5.5 sm:mb-6 flex-grow font-semibold">{item.desc}</p>
                  <div className="text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] text-[#475569] mb-4 xs:mb-4.5 sm:mb-5 pb-4 xs:pb-4.5 sm:pb-5 border-b-2 border-[#f1f5f9] flex justify-between items-center font-semibold">
                    <span>Leverage (Up to)</span>
                    <span className="font-extrabold text-[#0f172a] bg-[#f1f5f9] py-1 px-2.5 xs:px-3 rounded-[20px] text-[0.75rem] xs:text-[0.8rem] sm:text-[0.85rem]">{item.leverage}</span>
                  </div>
                  <Link to="/register" className="w-full py-2.5 xs:py-2.75 sm:py-3 px-5 xs:px-5.5 sm:px-6 bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white border-none rounded-[30px] xs:rounded-[35px] sm:rounded-[40px] text-[0.8rem] xs:text-[0.85rem] sm:text-[0.95rem] font-extrabold cursor-pointer transition-all duration-300 shadow-lg shadow-[#7c3aed]/20 flex items-center justify-center gap-1 xs:gap-1.5 hover:scale-[1.03] hover:shadow-xl hover:shadow-[#7c3aed]/30 no-underline text-center">
                    <i className="fas fa-chart-line" /> Trade
                  </Link>
                </div>
              ))}
            </div>

            <div className="w-full h-[4px] xs:h-[4.5px] sm:h-[5px] bg-[#e2e8f0] rounded-[10px] my-6 xs:my-7 sm:my-8 overflow-hidden">
              <div className="h-full bg-[#8b5cf6] rounded-[10px] transition-all duration-400" style={{ width: `${scrollProgress}%` }} />
            </div>
          </div>

          <div className="flex justify-center mt-4 xs:mt-5 sm:mt-6" data-aos="fade-up" data-aos-delay="300">
            <Link to="/register" className="inline-flex items-center gap-1.5 xs:gap-1.5 sm:gap-2 py-[0.7rem] xs:py-[0.75rem] sm:py-[0.8rem] lg:py-[0.9rem] px-[1.8rem] xs:px-[2.2rem] sm:px-[2.4rem] lg:px-[2.8rem] border-2 border-[#8b5cf6] bg-transparent text-[#8b5cf6] rounded-[50px] text-xs xs:text-sm sm:text-base font-extrabold cursor-pointer transition-all duration-300 hover:bg-[#8b5cf6] hover:text-white hover:-translate-y-1 no-underline">
              <i className="fas fa-book-open" /> Learn More
            </Link>
          </div>
        </section>

        {/* ════════════════ TRADING SIMPLE HERO ════════════════ */}
        <section id="trading-simple" className="relative w-full min-h-[70vh] xs:min-h-[75vh] sm:min-h-screen flex items-center justify-between py-[40px] xs:py-[50px] sm:py-[60px] px-[15px] xs:px-[20px] sm:px-10 bg-cover bg-center bg-no-repeat bg-[#1e1345] overflow-hidden" style={{ backgroundImage: "url('./src/assets/img/bottom.png')" }}>
          <div className="absolute inset-0 bg-[#1e1345]/50 z-[1]" />
          <div className="relative z-[2] flex items-center justify-between w-full max-w-[1400px] mx-auto gap-8 xs:gap-10 sm:gap-10 max-lg:flex-col max-lg:gap-4 xs:max-lg:gap-5">
            <div className="flex-1 max-w-[500px] max-lg:max-w-full max-lg:text-center" data-aos="fade-right">
              <h1 className="text-[clamp(2rem,6vw,72px)] font-extrabold text-white leading-[1.1] tracking-[-1.5px] xs:tracking-[-2px] mb-[40px] xs:mb-[50px] sm:mb-[60px] max-lg:mb-8 xs:max-lg:mb-10 sm:max-lg:mb-10 uppercase">
                Trading Made Simple
              </h1>
            </div>
            <div className="flex-1 flex flex-col items-end gap-[20px] xs:gap-[22px] sm:gap-[25px] max-lg:items-center">
              <p className="text-right text-white text-sm xs:text-base sm:text-lg leading-[1.6] font-semibold max-w-[250px] max-lg:text-center max-lg:max-w-[300px]" data-aos="fade-left">
                Open an account with Anzo Trade today
              </p>
              <Link to="/register" className="py-[10px] xs:py-[12px] sm:py-[14px] px-[35px] xs:px-[40px] sm:px-[45px] bg-white text-[#7c3aed] border-none rounded-[50px] text-xs xs:text-sm sm:text-base font-extrabold cursor-pointer transition-all duration-300 uppercase tracking-[0.4px] xs:tracking-[0.5px] hover:scale-105 hover:shadow-xl hover:shadow-white/20 no-underline" data-aos="fade-left" data-aos-delay="200">
                Trade Now
              </Link>
            </div>
          </div>
        </section>

        {/* ════════════════ FOOTER ════════════════ */}
        <footer className="bg-gradient-to-br from-[#7c3aed] via-[#8b5cf6] to-[#6366f1] text-white py-[clamp(2.5rem,8vw,6rem)] px-[clamp(1rem,4vw,2.5rem)] pb-6 xs:pb-7 sm:pb-8">
          <div className="max-w-[1350px] mx-auto">
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 xs:gap-7 sm:gap-8 mb-10 xs:mb-12">
              <div className="flex flex-col gap-4 xs:gap-4 sm:gap-5" data-aos="fade-up">
                <img src="/src/assets/img/logo/logo-white.svg" alt="Anzo Trade" className="object-contain brightness-0 invert" style={{ width: 'clamp(80px, 10vw, 130px)', height: 'clamp(60px, 7.5vw, 98px)' }} />
                <p className="text-[0.85rem] xs:text-[0.9rem] sm:text-[0.95rem] text-white/70 leading-[1.6] max-w-[280px] max-sm:max-w-full font-semibold">Your gateway to global markets. Trade with confidence, backed by advanced technology and expert support.</p>
                <div className="flex gap-3 xs:gap-3.5 sm:gap-4 mt-3 xs:mt-4">
                  {['twitter', 'linkedin', 'facebook', 'instagram'].map(s => (
                    <a key={s} href="#" className="w-9 h-9 xs:w-9.5 xs:h-9.5 sm:w-10 sm:h-10 flex items-center justify-center bg-white/15 rounded-full text-white no-underline transition-all duration-300 hover:bg-white/25 hover:-translate-y-1.5 hover:rotate-[10deg]">
                      <i className={`fab fa-${s} text-[0.9rem] xs:text-[0.95rem] sm:text-[1rem]`} />
                    </a>
                  ))}
                </div>
              </div>

              {[
                { title: 'Trading', links: ['Forex', 'Stocks', 'Commodities', 'Indices', 'Crypto CFDs', 'Metals'] },
                { title: 'Resources', links: ['Education', 'Market Insights', 'Tools', 'Trading Guides', 'API Documentation', 'Blog'] },
                { title: 'Company', links: ['About Us', 'Contact Us', 'Careers', 'News & Press', 'Partners', 'Affiliates'] },
              ].map((col, i) => (
                <div key={i} data-aos="fade-up" data-aos-delay={100 + i * 100}>
                  <h3 className="text-[0.95rem] xs:text-[1rem] sm:text-[1.05rem] lg:text-[1.1rem] font-extrabold mb-3 xs:mb-4 sm:mb-5 tracking-[-0.3px]">{col.title}</h3>
                  <ul className="list-none flex flex-col gap-2">
                    {col.links.map((link, j) => (
                      <li key={j}><a href="#" className="text-white/80 no-underline text-[0.8rem] xs:text-[0.85rem] sm:text-[0.9rem] lg:text-[0.95rem] font-semibold transition-all duration-300 hover:text-white hover:pl-1.5">{link}</a></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-6 xs:pt-7 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 xs:gap-4 text-xs xs:text-sm text-white/60 font-semibold">
              <p>© {new Date().getFullYear()} Anzo Trade. All rights reserved.</p>
              <div className="flex gap-4 xs:gap-5 sm:gap-6">
                <a href="#" className="text-white/60 no-underline hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-white/60 no-underline hover:text-white transition-colors">Terms of Service</a>
                <a href="#" className="text-white/60 no-underline hover:text-white transition-colors">Risk Disclosure</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AnzoTradeLanding;